/**
 * Détecte l’inclusion probable de coordonnées (e-mail, tel. FR, liens, messageries) dans un texte libre
 * pour limiter le contournement de RdvMarket (saisie, collage, requêtes directes).
 */

export const messageCoordonneesInterdites =
  "Les coordonnées (e-mail, téléphone, liens ou messageries) ne sont pas autorisées dans ce champ. Utilisez l’espace d’échange de la plateforme.";

/**
 * @param {unknown} text
 * @returns {boolean}
 */
export function texteContientCoordonnees(text) {
  if (text == null) return false;
  let t = String(text);
  if (t.trim() === "") return false;
  try {
    t = t.normalize("NFKC");
  } catch {
    /* */
  }
  t = t.replace(/[\u200B-\u200D\uFEFF\u2060]/g, "");
  /** Chiffres alternés avec symboles, espaces, tirets, ou d’autres scripts (٠٦…, ０-９, ⓪…) → ASCII pour analyse */
  t = reecrireChiffresEnAscii(t);

  if (/[@＠]/.test(t)) return true;
  if (/\S+@\S+\.[\p{L}\p{M}\d]{2,}/u.test(t)) return true;
  if (/\S+＠\S+/.test(t)) return true;

  if (/(?:\s|^)[\w.+\-–]{1,32}\s*(?:\(at\)|\[@\]|\[at\]|'at'|dot\s*at)\s*[\w.\-–]+/i.test(t)) {
    return true;
  }
  if (/(?:\s|^)at\s+[\w.+\-–]{1,20}\s+(?:dot|point)\s+[\p{L}]{2,4}(?:\s|[),.;]|$)/iu.test(t)) {
    return true;
  }

  if (/(?:https?|ftps?|s?ftp|www\.)|:\/\/|www\.\S{2,}/i.test(t)) {
    if (/(?:^|\s)https?:\/\//i.test(t) || /www\.\S{2,}/i.test(t) || /:\/\/\S/.test(t)) {
      return true;
    }
  }
  if (/(?:[a-z0-9-]+\.)+(?:com|fr|net|org|app|io|be|ch|de|es|it|uk|me)\/[^\s)]{0,200}/i.test(t)) {
    return true;
  }
  if (/(?:\s|\/|\.|^)(t\.me|wa\.me|linktr\.ee|bento\.me|m\.me|l\.w\.me|bit\.ly|cutt\.ly|tinyurl|qr\.|discord\.(gg|com|me|io))(?:\/|\.|\?|\s|$)/i.test(t)) {
    return true;
  }
  if (/(?:\s|^)(?:whatsapp|telegram|signal|skype|viber|snapchat|messenger|instagram\.com|facebook\.com)\b/i.test(t)) {
    return true;
  }

  if (chaineContientNumeroFrMobile(deriveChainesChiffresTelephone(t))) {
    return true;
  }

  if (/(?:^|[^\d])\+[\s(]*0?3{2}[\s).\-]*[1-9]/.test(t) || /0033[\s.\-]*[1-9]/.test(t) || /\+\s*3{2}[\s.\-]*[1-9]/.test(t)) {
    return true;
  }

  return false;
}

/**
 * Tout chiffre décimal Unicode (Nd) + chiffres encerclés / parenthésés → caractères 0-9.
 * Ainsi, mélangés avec . ! ? — · / \\ etc., la file des chiffres reconstitue le numéro.
 * @param {string} t
 * @returns {string}
 */
function reecrireChiffresEnAscii(t) {
  let r = "";
  for (const ch of t) {
    const n = chiffreUnicodeVersAscii0a9(ch);
    if (n != null) {
      r += n;
      continue;
    }
    r += ch;
  }
  return r;
}

/** Blocs de 10 caractères Nd (chiffre décimal) en Unicode — mêmes « formes visuelles que 0-9 mêlées à symboles, lettres, etc. */
const BLOCS_CHIFFRES_10 = [
  0x30, 0x660, 0x6f0, 0x7c0, 0x966, 0x9e6, 0xa66, 0xae6, 0xb66, 0xbe6, 0xc66, 0xce6, 0xd66, 0xde6, 0xe50, 0xed0, 0xf20, 0x1040, 0x17e0, 0x1a80, 0x1a90, 0x1b50, 0x1bb0, 0x1c40, 0x1c50, 0xa620, 0xa8d0, 0xa8e0, 0xa900, 0xa9d0, 0xa9f0, 0xaa50, 0xabf0, 0xff10, 0x104a0, 0x10d30, 0x110f0, 0x11066, 0x111d0, 0x11136, 0x111e0, 0x112f0, 0x114d0, 0x11650, 0x116c0, 0x118e0, 0x11c50, 0x11d50, 0x11da0, 0x11fc0, 0x16a60, 0x16b50, 0x1d7ce, 0x1d7d8, 0x1d7e2, 0x1d7ec, 0x1d7f6, 0x1e140, 0x1e2f0, 0x1e950, 0x1f100,
];

/**
 * @param {string} ch un point de code (itération sur la chaîne, y compris astral)
 * @returns {string | null} 0-9, ou n° à plusieurs chiffres encerclé (ex. « 10 »), ou null
 */
function chiffreUnicodeVersAscii0a9(ch) {
  if (!ch) {
    return null;
  }
  let p = ch.codePointAt(0);
  let c0 = ch;
  try {
    c0 = ch.normalize("NFC");
    p = c0.codePointAt(0) ?? p;
  } catch {
    /* */
  }
  for (const base of BLOCS_CHIFFRES_10) {
    if (p >= base && p < base + 10) {
      return String.fromCodePoint(0x30 + (p - base));
    }
  }
  if (p === 0x24ea) {
    return "0";
  }
  /** ①-⑳ (une étiquette = 10, 20, etc. écrits côte à côte pour la fenêtre 10 chiffres) */
  if (p >= 0x2460 && p <= 0x2473) {
    return String(p - 0x2460 + 1);
  }
  if (p === 0x3251) {
    return "21";
  }
  return null;
}

/** 0 (lettre ou lookalike) : à normaliser en premier */
const R_ZERO_LETTRE = /[oOΟοоօՕоℴØø∅○●𝟎𝟘⓪０օＯ０]/g;

const G = 32;
const ETIRE_LEET = [
  // espacement large : chiffre ··· pièces ··· leet ··· chiffre (jusqu’à ~32 car. non chiffre entre eux)
  [new RegExp(String.raw`(\d)\D{0,${G}}[lI|ｉіⅼ!¡]\D{0,${G}}(\d)`), (m, a, b) => a + "1" + b],
  [new RegExp(String.raw`(\d)\D{0,${G}}(?:S|＄|￥)\D{0,${G}}(\d)`), (m, a, b) => a + "5" + b],
  [new RegExp(String.raw`(\d)\D{0,${G}}(?:B|b|Ｂ|８)\D{0,${G}}(\d)`), (m, a, b) => a + "8" + b],
  [new RegExp(String.raw`(\d)\D{0,${G}}(?:Z|z|Ｚ)\D{0,${G}}(\d)`), (m, a, b) => a + "2" + b],
  [new RegExp(String.raw`(\d)\D{0,${G}}(?:G|g|Ｇ)\D{0,${G}}(\d)`), (m, a, b) => a + "6" + b],
  [new RegExp(String.raw`(\d)\D{0,${G}}(?:Q|q|Ｑ)\D{0,${G}}(\d)`), (m, a, b) => a + "9" + b],
  [new RegExp(String.raw`(\d)\D{0,${G}}(?:@|A|Ａ)\D{0,${G}}(\d)`), (m, a, b) => a + "4" + b],
];

/**
 * Chaîne telle qu’un humain tente d’espace ou de mêler chiffres et lettres (l33t).
 * Plusieurs passes pour enchaîner 6l7S8.
 */
function chiffresAvecFausseLettre(t) {
  let s = t.replace(R_ZERO_LETTRE, "0");
  for (let round = 0; round < 8; round += 1) {
    const avant = s;
    for (const [re, repl] of ETIRE_LEET) {
      s = s.replace(re, repl);
    }
    s = s.replace(R_ZERO_LETTRE, "0");
    if (s === avant) {
      break;
    }
  }
  return s.replace(/[^\d]/g, "");
}

/**
 * Diverses façons de cacher 10+ chiffres d’un n° FR (côte à côte, espacés, lettres entre chiffres, leet).
 * @param {string} t texte normalisé
 * @returns {string[]}
 */
function deriveChainesChiffresTelephone(t) {
  const ens = new Set();
  const add = (x) => {
    if (typeof x === "string" && x.length) ens.add(x);
  };
  add(t.replace(/[^\d]/g, ""));
  add(chiffresAvecFausseLettre(t));
  return Array.from(ens);
}

/**
 * @param {string[]} extraits
 */
function chaineContientNumeroFrMobile(extraits) {
  for (const d0 of extraits) {
    const d = d0;
    for (let i = 0; i <= d.length - 10; i++) {
      if (/^0[1-9]\d{8}$/.test(d.slice(i, i + 10))) {
        return true;
      }
    }
    for (let i = 0; i <= d.length - 11; i++) {
      if (/^33[1-9]\d{8}$/.test(d.slice(i, i + 11))) {
        return true;
      }
    }
    for (let i = 0; i <= d.length - 12; i++) {
      if (/^0033[1-9]\d{8}$/.test(d.slice(i, i + 12))) {
        return true;
      }
    }
  }
  return false;
}
